import React, { useEffect, useState } from "react";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { getPaymentSummary } from "./PaymentPanel";

const PAID_COLOR = "#19a86d";
const PARTIAL_COLOR = "#dfad1d";
const UNPAID_COLOR = "#de4141";

const useStyles = makeStyles((theme) => ({
  // Zero-height sticky anchor, so the bar overlays the page instead of
  // pushing it down when it appears. Sits right under the app bar.
  anchor: {
    position: "sticky",
    top: 56,
    height: 0,
    // Full row when placed inside a Grid container (a flex row)
    width: "100%",
    zIndex: theme.zIndex.appBar - 1,
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      top: 48,
    },
    [theme.breakpoints.up("sm")]: {
      top: 64,
    },
  },
  bar: {
    padding: theme.spacing(1, 2),
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1, 2),
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: theme.transitions.duration.short,
    }),
  },
  hidden: {
    opacity: 0,
    transform: "translateY(-8px)",
    pointerEvents: "none",
  },
  group: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const balanceChip = (summary) => {
  if (summary.isOverpaid)
    return {
      label: `Refund due ${currencyFormat.format(summary.overpaid)}`,
      color: UNPAID_COLOR,
    };
  if (summary.isPaid) return { label: "Paid in full", color: PAID_COLOR };
  return {
    label: `Owed ${currencyFormat.format(summary.amountRemaining)}`,
    color: summary.netAmountPaid > 0 ? PARTIAL_COLOR : UNPAID_COLOR,
  };
};

// Slim summary that stays at the top while scrolling a long order, with jump
// links to each section. Hidden until `watchId` (the overview) scrolls away.
const OrderStickyBar = ({ orderId, order, watchId, sections }) => {
  const classes = useStyles();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const target = document.getElementById(watchId);
    if (!target || typeof IntersectionObserver === "undefined") return;

    // The negative top margin accounts for the app bar covering the page top
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: "-64px 0px 0px 0px" }
    );
    observer.observe(target);

    return () => observer.disconnect();
  }, [watchId]);

  const summary = getPaymentSummary(order.amounts || {}, order.type);
  const balance = balanceChip(summary);

  const jumpTo = (id) => {
    const section = document.getElementById(id);
    if (section) section.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className={classes.anchor} aria-hidden={!isVisible}>
      <Paper
        elevation={6}
        className={`${classes.bar} ${isVisible ? "" : classes.hidden}`}
      >
        <div className={classes.group}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Order #{orderId}
          </Typography>
          {order.customer_name && (
            <Typography variant="body2" color="textSecondary">
              {order.customer_name}
            </Typography>
          )}
          {order.order_status && (
            <Chip size="small" label={`Order: ${order.order_status}`} />
          )}
          {order.reservation_status && (
            <Chip
              size="small"
              label={`Reservation: ${order.reservation_status}`}
            />
          )}
          <Chip
            size="small"
            label={balance.label}
            style={{
              backgroundColor: balance.color,
              color: summary.isOverpaid ? "#fff" : "#000",
              fontWeight: 600,
            }}
          />
        </div>
        <Box className={classes.group}>
          {sections.map(({ id, label }) => (
            <Button
              key={id}
              size="small"
              color="primary"
              tabIndex={isVisible ? 0 : -1}
              onClick={() => jumpTo(id)}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Paper>
    </div>
  );
};

export default OrderStickyBar;
